import styles from './index.module.css'

import * as React from 'react'

import { ALBUMS, SHOWCASES, SIM_SEARCH } from '@disco/constants/FeatureTypes'
import type { SearchType } from '@disco/constants/search'
import {
  getSearchResults,
  updateSearchParams,
} from '@disco/state-management/actions/SearchActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getDiscoTrackUrl } from '@disco/utils/getTrackSearchURL'
import { getYouTubeId } from '@disco/utils/getVideoEmbedURL'
import { getFiltersWithValidValues } from '@disco/utils/search'

import { Tabs, Tooltip } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { Option } from '../select-options'
import { searchTypeOptions } from '../select-options'

/** A pasted YouTube or DISCO track URL is what makes a search a similarity search. */
const isSimilarityUrl = (term: string): boolean =>
  Boolean(getYouTubeId(term) || getDiscoTrackUrl(term))

// A full sentence per type rather than one interpolated string, so the copy
// stays translatable.
const NON_TRACK_TOOLTIPS: Record<string, string> = {
  '': 'To use the all filter, clear the metadata filter, track tags, or duration filter.',
  playlist:
    'To use the playlist filter, clear the metadata filter, track tags, or duration filter.',
  album:
    'To use the album filter, clear the metadata filter, track tags, or duration filter.',
}

/**
 * Result-type tabs (All / Tracks / Playlists / Albums / Artists) shown in the
 * Browse search pane header, so the type stays switchable after a search runs.
 */
const SearchTypeFilter = (): React.ReactElement => {
  const dispatch = useDispatch()

  const {
    type: searchType,
    searchTerm,
    filters,
    groups,
    tags,
    nativeTags,
    startDate,
    duration,
  } = useSelector(state => state.search)

  const simSearchEnabled = useFeatureEnabled(SIM_SEARCH)
  const albumsEnabled = useFeatureEnabled(ALBUMS)
  const showcasesEnabled = useFeatureEnabled(SHOWCASES)

  // Read off the term, not searchQuery.simSearch, which only lands in the
  // store once the request succeeds.
  const isSimSearch = simSearchEnabled && isSimilarityUrl(searchTerm)

  // Metadata filters, track tags and duration only ever apply to tracks, so
  // All/Playlists/Albums must be blocked whenever any of them is set.
  const disableNonTrackOptions = Boolean(
    getFiltersWithValidValues(filters).length > 0 ||
      nativeTags.length ||
      duration !== null,
  )
  const disableArtistOption = Boolean(
    disableNonTrackOptions ||
      groups.length ||
      tags.length ||
      startDate.getTime(),
  )

  const options: Option[] = searchTypeOptions
    .filter(option => {
      if (option.value === 'artist') return showcasesEnabled
      if (option.value === 'album') return albumsEnabled
      return true
    })
    .map(option => {
      // Similarity search only ever returns tracks, so lock the menu to Tracks.
      if (isSimSearch) {
        return option.value === 'track'
          ? option
          : {
              ...option,
              disabled: true,
              tooltip: 'Similarity search only works with tracks.',
            }
      }

      switch (option.value) {
        case '':
        case 'playlist':
        case 'album':
          return {
            ...option,
            disabled: disableNonTrackOptions,
            tooltip: disableNonTrackOptions
              ? NON_TRACK_TOOLTIPS[option.value]
              : undefined,
          }
        case 'artist':
          return {
            ...option,
            disabled: disableArtistOption,
            tooltip: disableArtistOption
              ? 'To use the artist filter, clear the other search terms.'
              : undefined,
          }
        default:
          return option
      }
    })

  const handleValueChange = (value: string) => {
    if (options.find(option => option.value === value)?.disabled) return
    const type = (value as SearchType | '') || false
    dispatch(updateSearchParams({ type }))
    dispatch(getSearchResults())
  }

  return (
    <Tabs.Root value={searchType || ''} onValueChange={handleValueChange}>
      <Tabs.List size="1" className={styles.tabsList}>
        {options.map(option => {
          const trigger = (
            <Tabs.Trigger
              key={option.value}
              value={option.value}
              // A native disabled button emits no pointer events, so the
              // tooltip explaining the disabled state would never open.
              aria-disabled={option.disabled}
              className={option.disabled ? styles.disabled : undefined}
            >
              {option.label}
            </Tabs.Trigger>
          )
          return option.tooltip ? (
            <Tooltip key={option.value} content={option.tooltip}>
              {trigger}
            </Tooltip>
          ) : (
            trigger
          )
        })}
      </Tabs.List>
    </Tabs.Root>
  )
}

export default SearchTypeFilter
